import { VisitSettlementStatus } from '@dreryk/egabinet-financial-settlement-frontend-api';

import { Translate } from '@dreryk/react-common';
import { PaymentsIcon, StandardSpinner, Text, TextSeparator, TextSeparatorType } from '@dreryk/react-components';

import { AdvancePaymentStatusIcon, AdvancePaymentStatusText } from '~libs.desktop.shared.appointment-advance-payment';

import { PriceText } from '../../../visitSettlement/shared/price/PriceText';
import { prescriptionVisitsTranslationKeys } from '../../../translations';
import { usePrescriptionVisitSettlementGlobalState } from '../settlement/usePrescriptionVisitSettlementGlobalState';
import { usePrescriptionVisitAdvancePaymentGlobalState } from '../settlement/usePrescriptionVisitAdvancePaymentGlobalState';

import { isAdvancePaymentVisible } from './PrescriptionVisitSettlementCell.utils';
import {
   StyledAmountWrapper,
   StyledPrescriptionVisitSettlementIcon,
   StyledSettlementCellContainer,
   StyledSettlementRow,
} from './PrescriptionVisitSettlementCell.styled';

type PrescriptionVisitSettlementCellProps = {
   readonly appointmentId: number;
   readonly onlinePaymentsIntegrationExists: boolean;
};

export const PrescriptionVisitSettlementCell = (props: PrescriptionVisitSettlementCellProps): JSX.Element => {
   const { getSettlement } = usePrescriptionVisitSettlementGlobalState();
   const { getAdvancePayment } = usePrescriptionVisitAdvancePaymentGlobalState();
   const visitSettlement = getSettlement(props.appointmentId);
   const advancePayment = getAdvancePayment(props.appointmentId);
   const status: VisitSettlementStatus | null = visitSettlement?.settlementStatus ?? null;
   const showAdvancePayment = isAdvancePaymentVisible(advancePayment, props.onlinePaymentsIntegrationExists);

   return (
      <StyledSettlementCellContainer>
         <StyledSettlementRow>
            <StyledPrescriptionVisitSettlementIcon $status={status}>
               {status ? <PaymentsIcon /> : <StandardSpinner />}
            </StyledPrescriptionVisitSettlementIcon>
            {status ? (
               <Translate>{prescriptionVisitsTranslationKeys.settlement.statusDescriptionMap[status]}</Translate>
            ) : null}
            {visitSettlement ? (
               <StyledAmountWrapper>
                  <TextSeparator separator={TextSeparatorType.SPACE_SLASH_SPACE}>
                     <PriceText
                        testId={'paidAmount'}
                        value={visitSettlement.totalPaidAmount.asNumber()}
                        stripDecimalZeroes
                        hideCurrency
                     />
                     <PriceText
                        testId={'requiredAmount'}
                        value={visitSettlement.totalDueAmount.asNumber()}
                        stripDecimalZeroes
                     />
                  </TextSeparator>
               </StyledAmountWrapper>
            ) : null}
         </StyledSettlementRow>
         {showAdvancePayment && advancePayment ? (
            <StyledSettlementRow>
               <AdvancePaymentStatusIcon
                  paidAmount={advancePayment.paidAmount}
                  requiredAmount={advancePayment.requiredAmount}
               />
               <Text testId={'advancePayment'}>
                  <Translate>{prescriptionVisitsTranslationKeys.settlement.advancePayment}</Translate>
               </Text>
               <AdvancePaymentStatusText
                  paidAmount={advancePayment.paidAmount}
                  requiredAmount={advancePayment.requiredAmount}
               />
               <StyledAmountWrapper>
                  <TextSeparator separator={TextSeparatorType.SPACE_SLASH_SPACE}>
                     <PriceText
                        testId={'advancePaymentPaidAmount'}
                        value={advancePayment.paidAmount.asNumber()}
                        stripDecimalZeroes
                        hideCurrency
                     />
                     <PriceText
                        testId={'advancePaymentRequiredAmount'}
                        value={advancePayment.requiredAmount.asNumber()}
                        stripDecimalZeroes
                     />
                  </TextSeparator>
               </StyledAmountWrapper>
            </StyledSettlementRow>
         ) : null}
      </StyledSettlementCellContainer>
   );
};
